import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CountrySelect } from '@/components/ui/country-select'
import { BRAND_MINT } from '@/lib/utils'

import {
  getOrganizationProfileApi,
  updateOrganizationProfileApi,
  verifyOrganizationApi,
  getCountriesApi,
  type Country,
} from '@/lib/api'
import {
  ArrowLeftIcon,
  PlusIcon,
  SaveIcon,
  Trash2Icon,
  XIcon,
  AlertTriangleIcon,
  ShieldCheckIcon,
  LockIcon,
} from 'lucide-react'

interface PhoneEntry {
  key: number
  phone_number: string
  description: string
}

export const Route = createFileRoute('/dashboard/company-profile/edit')({
  component: () => {
    const navigate = useNavigate()
    const [orgName, setOrgName] = useState('')
    const [logoUrl, setLogoUrl] = useState('')
    const [legalEntityName, setLegalEntityName] = useState('')
    const [registrationNumber, setRegistrationNumber] = useState('')
    const [dunsNumber, setDunsNumber] = useState('')
    const [vatNumber, setVatNumber] = useState('')
    const [registeredAddress, setRegisteredAddress] = useState('')
    const [billingEmail, setBillingEmail] = useState('')
    const [countryId, setCountryId] = useState<number | ''>('')
    const [countries, setCountries] = useState<Country[]>([])
    const [phoneEntries, setPhoneEntries] = useState<PhoneEntry[]>([])
    const [nextPhoneKey, setNextPhoneKey] = useState(0)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Option A: track profile completeness
    const [profileMissing, setProfileMissing] = useState(false)
    const [needsVerification, setNeedsVerification] = useState(false)
    const [profileJustCompleted, setProfileJustCompleted] = useState(false)
  const [submittingVerification, setSubmittingVerification] = useState(false)

    useEffect(() => {
      let cancelled = false

      async function load() {
        try {
          const [orgRes, countriesRes] = await Promise.all([
            getOrganizationProfileApi(),
            getCountriesApi(),
          ])
          if (cancelled) return

          const org = orgRes.organization
          setOrgName(org.org_name)
          setLogoUrl(org.logo_url || '')
          setLegalEntityName(org.legal_entity_name || '')
          setRegistrationNumber(org.registration_number || '')
          setDunsNumber(org.duns_number || '')
          setVatNumber(org.vat_number || '')
          setRegisteredAddress(org.registered_address || '')
          setBillingEmail(org.billing_email || '')
          if (org.country_id) {
            setCountryId(org.country_id)
          }
          setCountries(countriesRes.data)

          // Check profile completeness (VAT or registration number + registered address)
          const hasTaxId =
            (org.vat_number && org.vat_number.trim().length > 0) ||
            (org.registration_number && org.registration_number.trim().length > 0)
          const hasAddress = org.registered_address && org.registered_address.trim().length > 0
          const isVerified = org.verified_status === 'active'

          if (!hasTaxId || !hasAddress) {
            setProfileMissing(true)
          }
          if (!isVerified) {
            setNeedsVerification(true)
          }

          // Initialise phone entries from existing data
          const phones = org.phone_numbers || []
          if (phones.length > 0) {
            setPhoneEntries(
              phones.map((pn, i) => ({
                key: i,
                phone_number: pn.phone_number,
                description: pn.description || '',
              }))
            )
            setNextPhoneKey(phones.length)
          }
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load company profile')
            toast.error(err instanceof Error ? err.message : 'Failed to load company profile')
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => { cancelled = true }
    }, [])

    const addPhone = () => {
      setPhoneEntries((prev) => [
        ...prev,
        { key: nextPhoneKey, phone_number: '', description: '' },
      ])
      setNextPhoneKey((k) => k + 1)
    }

    const removePhone = (key: number) => {
      setPhoneEntries((prev) => prev.filter((p) => p.key !== key))
    }

    const updatePhone = (key: number, field: 'phone_number' | 'description', value: string) => {
      setPhoneEntries((prev) =>
        prev.map((p) => (p.key === key ? { ...p, [field]: value } : p))
      )
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (!orgName.trim()) {
        toast.error('Company name is required')
        return
      }

      setSaving(true)
      try {
        await updateOrganizationProfileApi({
          org_name: orgName.trim(),
          logo_url: logoUrl.trim() || null,
          legal_entity_name: legalEntityName.trim() || null,
          registration_number: registrationNumber.trim() || null,
          duns_number: dunsNumber.trim() || null,
          vat_number: vatNumber.trim() || null,
          registered_address: registeredAddress.trim() || null,
          billing_email: billingEmail.trim() || null,
          country_id: countryId ? Number(countryId) : undefined,
          phone_numbers: phoneEntries
            .filter((p) => p.phone_number.trim())
            .map((p) => ({
              phone_number: p.phone_number.trim(),
              description: p.description.trim() || null,
            })),
        })
        // Check if profile is now complete after save
        const hasTaxId = vatNumber.trim().length > 0 || registrationNumber.trim().length > 0
        const hasAddress = registeredAddress.trim().length > 0
        if ((profileMissing || needsVerification) && hasTaxId && hasAddress) {
          setProfileJustCompleted(true)
          setNeedsVerification(false)
        }
        setProfileMissing(false)
        toast.success('Company profile updated')
        navigate({ to: '/dashboard/company-profile' })
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save')
      } finally {
        setSaving(false)
      }
    }

    if (error) {
      return (
        <div className="mx-auto max-w-2xl">
          <div className="mb-8">
            <Button
              type="button"
              variant="ghost"
              size="sm"
            className="mb-4 -ml-2 gap-1.5"
            onClick={() => navigate({ to: '/dashboard/company-profile' })}
          >
            <ArrowLeftIcon className="size-4" />
            Back to company profile
          </Button>
        </div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
            <p className="text-sm text-destructive">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => navigate({ to: '/dashboard/company-profile' })}
            >
              Go back
            </Button>
          </div>
        </div>
      )
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-2xl animate-pulse">
          <div className="mb-8">
            <div className="h-7 w-48 rounded bg-muted" />
          </div>
          <div className="space-y-6 rounded-xl border border-border p-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-24 rounded bg-muted" />
                <div className="h-10 w-full rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-2xl">
        {/* Option A: Banner for incomplete org profile */}
        {profileMissing && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/40 dark:bg-amber-950/20">
            <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Company profile required
              </p>
              <p className="mt-0.5 text-sm text-amber-700 dark:text-amber-400">
                You must provide your <strong>VAT / registration number</strong> and{' '}
                <strong>Registered address</strong> before your organization can
                process payroll. Other features will remain locked until these
                are completed.
              </p>
            </div>
          </div>
        )}

        {/* Banner: needs admin verification */}
        {needsVerification && !profileMissing && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/40 dark:bg-blue-950/20">
            <ShieldCheckIcon className="mt-0.5 size-5 shrink-0 text-blue-600 dark:text-blue-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Awaiting verification
              </p>
              <p className="mt-0.5 text-sm text-blue-700 dark:text-blue-400">
                Your organization details have been submitted. An administrator
                needs to verify your account before you can fully use the platform.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/30"
              onClick={async () => {
                setSubmittingVerification(true)
                try {
                  await verifyOrganizationApi()
                  toast.success('Submitted for verification')
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to submit')
                } finally {
                  setSubmittingVerification(false)
                }
              }}
              disabled={submittingVerification}
            >
              {submittingVerification ? (
                <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <ShieldCheckIcon className="size-4" />
              )}
              Submit for verification
            </Button>
          </div>
        )}

        {/* Option A: Feature lock notice */}
        {profileMissing && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-border bg-muted/50 p-4">
            <LockIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">
                What's locked until your company profile is complete?
              </p>
              <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground space-y-0.5">
                <li>Employees cannot create Funding Circle requests</li>
                <li>Payment cycles cannot be initiated</li>
                <li>Payouts cannot be completed</li>
              </ul>
            </div>
          </div>
        )}

        {needsVerification && !profileMissing && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-border bg-muted/50 p-4">
            <LockIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">
                What's locked until verification is complete?
              </p>
              <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground space-y-0.5">
                <li>Employees cannot create Funding Circle requests</li>
                <li>Payment cycles cannot be initiated</li>
                <li>Payouts cannot be completed</li>
              </ul>
            </div>
          </div>
        )}

        <div className="mb-8">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-4 -ml-2 gap-1.5"
            onClick={() => navigate({ to: '/dashboard/company-profile' })}
          >
            <ArrowLeftIcon className="size-4" />
            Back to company profile
          </Button>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">Edit company profile</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Update your organization's legal identity, registered address, billing email, and contact information.
              </p>
            </div>
          </div>
        </div>

        <form className="space-y-8" onSubmit={handleSubmit}>
          <section className="space-y-6 rounded-xl border border-border bg-card p-6">
            <div className="space-y-2">
              <Label htmlFor="orgName">Company name</Label>
              <Input
                id="orgName"
                type="text"
                placeholder="Acme Inc."
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logoUrl">Logo URL</Label>
              <Input
                id="logoUrl"
                type="url"
                placeholder="https://example.com/logo.png"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Optional — a publicly accessible URL to your company logo.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="legalEntityName">Legal entity name</Label>
              <Input
                id="legalEntityName"
                type="text"
                placeholder="Acme Corp Ltd."
                value={legalEntityName}
                onChange={(e) => setLegalEntityName(e.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Optional — exact registered legal name. Falls back to the display name.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vatNumber">VAT number</Label>
              <Input
                id="vatNumber"
                type="text"
                placeholder="IE-1234567A"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="registrationNumber">Registration number</Label>
              <Input
                id="registrationNumber"
                type="text"
                placeholder="Company registration / EIN"
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                At least one of VAT number or registration number is required.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dunsNumber">D-U-N-S number</Label>
              <Input
                id="dunsNumber"
                type="text"
                placeholder="Dun & Bradstreet D-U-N-S number"
                value={dunsNumber}
                onChange={(e) => setDunsNumber(e.target.value)}
                disabled={saving}
                maxLength={9}
              />
              <p className="text-xs text-muted-foreground">
                Optional — 9-digit Dun &amp; Bradstreet business identifier.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="registeredAddress">Registered address</Label>
              <textarea
                id="registeredAddress"
                placeholder="123 Main St, City, State, 12345"
                value={registeredAddress}
                onChange={(e) => setRegisteredAddress(e.target.value)}
                disabled={saving}
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billingEmail">Billing email</Label>
              <Input
                id="billingEmail"
                type="email"
                placeholder="billing@acme.com"
                value={billingEmail}
                onChange={(e) => setBillingEmail(e.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Used for invoices and the Stripe customer record.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <CountrySelect
                id="country"
                value={countryId}
                onChange={setCountryId}
                countries={countries}
                disabled={saving}
                emptyLabel="Select a country"
              />
              <p className="text-xs text-muted-foreground">
                Used as the default country for new payment cycles.
              </p>
            </div>
          </section>

          {/* Phone numbers section */}
          <section className="space-y-4 rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold">Phone numbers</h2>
                <p className="text-xs text-muted-foreground">
                  Add one or more phone numbers with optional descriptions.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={addPhone}
                disabled={saving}
              >
                <PlusIcon className="size-4" />
                Add
              </Button>
            </div>

            {phoneEntries.length === 0 && (
              <p className="text-sm text-muted-foreground">No phone numbers added yet.</p>
            )}

            {phoneEntries.map((entry) => (
              <div key={entry.key} className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <Input
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={entry.phone_number}
                    onChange={(e) => updatePhone(entry.key, 'phone_number', e.target.value)}
                    disabled={saving}
                  />
                  <Input
                    type="text"
                    placeholder="Description (e.g. Main office, Support, Sales)"
                    value={entry.description}
                    onChange={(e) => updatePhone(entry.key, 'description', e.target.value)}
                    disabled={saving}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1 text-destructive hover:text-destructive"
                  onClick={() => removePhone(entry.key)}
                  disabled={saving}
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </div>
            ))}
          </section>

          <div className="flex items-center gap-4">
            <Button type="submit" disabled={saving} className="gap-1.5">
              {saving ? (
                <>
                  <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Saving…
                </>
              ) : (
                <>
                  <SaveIcon className="size-4" />
                  Save changes
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="gap-1.5"
              onClick={() => navigate({ to: '/dashboard/company-profile' })}
              disabled={saving}
            >
              <XIcon className="size-4" />
              Cancel
            </Button>
          </div>
        </form>
      </div>
    )
  },
})
